import React, { useState } from 'react';
import Alert from '@jbhi-fi/lanyard-ui/components/Alert/Alert';
import Button from '@jbhi-fi/lanyard-ui/components/Button/Button';
import Modal from '@jbhi-fi/lanyard-ui/components/Modal/Modal';
import TextField from '@jbhi-fi/lanyard-ui/components/TextField/TextField';
import Checkbox from '@jbhi-fi/lanyard-ui/components/Checkbox';
import { useSubscribe as useSubscribeDefault } from '../hooks/useKlaviyoSubscribe';
import { useBackInStoreSubscribe as useBackInStoreSubscribeDefault } from '../hooks/useKlaviyoBackInStoreSubscribe';
import { styles } from './NotifyMeModal.css';
import { InputType } from '@jbhi-fi/lanyard-ui/components/TextField/TextField.types';
import { UseKlaviyoSubscribe } from '../hooks/useKlaviyoSubscribe';
import { UseKlaviyoBackInStoreSubscribe } from '../hooks/useKlaviyoBackInStoreSubscribe';

export type NotifyMeModalProps = {
  isOpen: boolean;
  productTitle: string;
  productId: string;
  variantId: number;
  useSubscribe?: UseKlaviyoSubscribe;
  useBackInStoreSubscribe?: UseKlaviyoBackInStoreSubscribe;
  onClose: () => void;
};

const OFFERS_TEXT = 'Sign me up for instant deals and exclusive offers!';

export const NotifyMeModal = ({
  isOpen,
  productTitle,
  variantId,
  useSubscribe = useSubscribeDefault,
  useBackInStoreSubscribe = useBackInStoreSubscribeDefault,
  onClose,
}: NotifyMeModalProps) => {
  const {
    message: backInStoreMessage,
    isLoading: backInStoreLoading,
    hasError: backInStoreError,
    hasValidationError: backInStoreValidationError,
    onBackInStoreSubscribe,
  } = useBackInStoreSubscribe();

  const {
    message: subscribeMessage,
    isLoading: subscribeLoading,
    hasError: subscribeError,
    hasValidationError: subscribeValidationError,
    onSubscribe,
  } = useSubscribe();

  const [email, setEmail] = useState(window?.Jbhifi?.customerEmail ?? '');
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [specialOffersChecked, setSpecialOffersChecked] = useState<boolean>(false);

  const modalTitle = `Enter your email and we'll let you know as soon as the ${productTitle} is available.`;
  const successMessage = `You're in! We'll let you know when it's available. ${
    specialOffersChecked ? 'Our deals and offers will be beamed to your inbox, too.' : ''
  }`;

  const handleEmailChange = (value: string) => {
    setEmail(value);
  };
  const handleCheckboxChange = () => {
    setSpecialOffersChecked(!specialOffersChecked);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    await onBackInStoreSubscribe({ email, listId: variantId.toString() });
    if (specialOffersChecked) await onSubscribe({ email, listId: window.Jbhifi?.klaviyoMailingListId });
    !isFormSubmitted && setIsFormSubmitted(true);
  };
  const message = backInStoreMessage || (specialOffersChecked && subscribeMessage);
  const isLoading = backInStoreLoading || (specialOffersChecked && subscribeLoading);
  const hasError = backInStoreError || (specialOffersChecked && subscribeError);
  const hasValidationError = backInStoreValidationError || (specialOffersChecked && subscribeValidationError);

  const checkboxUniqueId = `specialOffersCheckbox ${Math.random().toString()}`; // re-use of this component causes the checkbox to break
  return (
    <Modal onClose={() => onClose()} show={isOpen} title="Notify me about this product" size="sm" zIndex={999999}>
      <p>{modalTitle}</p>

      <form onSubmit={handleSubmit}>
        <TextField
          className={styles.emailTextField}
          id="Notify Me Email"
          label="Email address"
          name="email"
          type={InputType.email}
          required={true}
          hasError={hasValidationError}
          errorMsg="Enter a valid email"
          onChange={e => handleEmailChange(e.target.value)}
          value={email}
        />
        <Checkbox
          id={checkboxUniqueId}
          name={checkboxUniqueId}
          label={OFFERS_TEXT}
          checked={specialOffersChecked}
          onChange={handleCheckboxChange}
        />
      </form>
      {!isFormSubmitted || hasError || hasValidationError ? (
        <Button
          loading={isLoading}
          label={message ? 'Try again' : 'Submit'}
          onClick={handleSubmit}
          type="primary"
          classes={styles.submitButton}
          fullWidth
        />
      ) : null}
      {message && !hasError && !hasValidationError ? <Alert type="success" content={successMessage} /> : null}
      {hasError ? <Alert type="error" content={`Sorry, there was an error with your submission.`} /> : null}
    </Modal>
  );
};
